import { useEffect, useState } from 'react';
import EyeOutlined from '@ant-design/icons/lib/icons/EyeOutlined';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { OpBanner } from 'new-components/DLS/OpBanner/OpBanner';
import { OpDrawer } from 'new-components/DLS/OpDrawer/OpDrawer';
import { OpTabs } from 'new-components/DLS/OpTabs/OpTabs';
import { useSelectorJs } from 'utils/customHooks';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import {
  kioskReadScopes,
  qrScanLogReadScopes,
  redFlagReadScopes,
  visitorAgreementReadScopes,
  visitorDocumentCheckReadScopes,
} from 'constants/visitorScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';

import {
  fetchRedFlags,
  RedFlagState,
  setSelectedRedFlaggedVisitor,
} from '../../../../store/slices/redFlagSlice';
import { describeVisitorWorkflowConfig } from '../../../../store/slices/visitorWorkflowsSlice';
import { AltaVideoState } from '../../../../store/slices/altaVideoSlice';
import { VisitsState } from '../../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { Agreement } from '../../../../types/agreementTypes';
import { DenyDetails } from '../../../../types/denyTypes';
import { Kiosk } from '../../../../types/kioskTypes';
import { QrCodeScanLog } from '../../../../types/qrCodeTypes';
import { DocumentCheck, VisitorAgreement } from '../../../../types/visitTypes';
import { isVisitorRedFlagged } from '../../../../utils/visitorsHelper';
import { DRAWER_WIDTH } from '../../../../constants/ui';
import warningIcon from '../../../../images/icons/warning.svg';

import ProfileDrawerHeader from './header/ProfileDrawerHeader';
import RedFlagMatchModal from './RedFlagMatchModal';
import ActivityLogTab from './tabs/ActivityLogTab';
import AgreementsTab from './tabs/AgreementsTab';
import ProfileTab from './tabs/ProfileTab';
import QrCodeTab from './tabs/QrCodeTab';

interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface LocationWorkflowConfig {
  SignInWorkflowId?: number;
  KioskInWorkflowId?: number;
}

interface VisitorTypeWorkflowConfig {
  workflow?: {
    SignInWorkflowId?: number;
    KioskInWorkflowId?: number;
  };
}

interface KioskLocation {
  id: number;
}

interface DenyEntryResponseItem {
  DenyDetails?: DenyDetails[];
}

const ProfileDrawer = ({ open, onClose }: ProfileDrawerProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { isValid: hasDenyRead } = useValidateScopes([
    'o{orgId}-visitorDeny:r',
    'o{orgId}-visitorVisitors:r',
    'o:r',
    's-o:r',
  ]);
  const { isValid: hasRedFlagRead } = useValidateScopes(redFlagReadScopes);
  const { isValid: hasDocumentCheckRead } = useValidateScopes(
    visitorDocumentCheckReadScopes,
  );
  const { isValid: hasQrScanLogRead } = useValidateScopes(qrScanLogReadScopes);
  const { isValid: hasVisitorAgreementRead } = useValidateScopes(
    visitorAgreementReadScopes,
  );
  const { isValid: hasKioskRead } = useValidateScopes(kioskReadScopes);
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const selectedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisitor']),
  ) as VisitsState['selectedVisitor'];
  const redFlags =
    (useSelectorJs((state: ImmutableRootState) =>
      state.getIn(['redFlags', 'redFlags']),
    ) as RedFlagState['redFlags']) || [];
  const isAltaVideoInstalled = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['altaVideo', 'isAltaVideoInstalled']),
  ) as AltaVideoState['isAltaVideoInstalled'];
  const devicesByWorkflow = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['altaVideo', 'devicesByWorkflow']),
  ) as AltaVideoState['devicesByWorkflow'];

  const [connectedKiosks, setConnectedKiosks] = useState<Kiosk[]>([]);
  const [visitorPhotoUrl, setVisitorPhotoUrl] = useState<string | null>(null);
  const [extendedVisitorInfo, setExtendedVisitorInfo] =
    useState<DocumentCheck | null>(null); // Extended visitor info includes additional properties like "photo" (e.g., "3090.jpg") and "agreement" (e.g., "3090_agreement.pdf")
  const [attendantAgreements, setAttendantAgreements] = useState<Agreement[]>(
    [],
  );
  const [kioskAgreements, setKioskAgreements] = useState<Agreement[]>([]);
  const [signedAgreements, setSignedAgreements] = useState<Agreement[]>([]);
  const [denyDetails, setDenyDetails] = useState<DenyDetails | undefined>(
    undefined,
  );
  const [qrCodeScanLogs, setQrCodeScanLogs] = useState<QrCodeScanLog[]>([]);

  const [activeKey, setActiveKey] = useState<string>('profile');
  const [isRedFlagMatchModalOpen, setIsRedFlagMatchModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Reset the active tab to 'profile' when the drawer opens
  useEffect(() => {
    if (open) {
      setActiveKey('profile');
    }
  }, [open]);

  useEffect(() => {
    const fetchWorkflowConfig = async () => {
      if (!orgId || !selectedVisit?.location?.id) {
        return;
      }

      try {
        const locationWorkflowResponse = await heliumReceive('locationConfig', [
          orgId,
          selectedVisit.location.id,
        ]);

        const locationVisitorTypeWorkflowResponse = await heliumReceive(
          'describeVisitorTypeWorkflow',
          [orgId, selectedVisit.location.id, selectedVisit?.visitorType.id],
        );

        const locationWorkflowData = Array.isArray(
          locationWorkflowResponse?.data,
        )
          ? (locationWorkflowResponse.data as LocationWorkflowConfig[])
          : [];
        const locationVisitorTypeWorkflowData = Array.isArray(
          locationVisitorTypeWorkflowResponse?.data,
        )
          ? (locationVisitorTypeWorkflowResponse?.data as VisitorTypeWorkflowConfig[])
          : [];

        const locationWorkflow = locationWorkflowData[0];
        const locationVisitorTypeWorkflow = locationVisitorTypeWorkflowData[0];

        const defaultSignInWorkflowId = locationWorkflow?.SignInWorkflowId;
        const visitorTypeSignInWorkflowId =
          locationVisitorTypeWorkflow?.workflow?.SignInWorkflowId;

        const workflowId =
          visitorTypeSignInWorkflowId !== -1
            ? visitorTypeSignInWorkflowId
            : defaultSignInWorkflowId;

        if (workflowId) {
          dispatch(
            describeVisitorWorkflowConfig({
              orgId,
              visitorWorkflowId: workflowId,
            }),
          );
        }
      } catch {
        // Failed to fetch SignInWorkflowId for describeVisitorWorkflowConfig
      }
    };

    fetchWorkflowConfig();
  }, [
    dispatch,
    orgId,
    selectedVisit?.location?.id,
    selectedVisit?.visitorType.id,
  ]);

  // Fetch data - optimized with parallel API calls
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Call each API individually with error handling.
        // Guarded on visitorAgreementReadScopes because the helium GET
        // visitorAgreement route rejects roles without visitor read (e.g.
        // dashboard-only) with a 403.
        const signedVisitorAgreementsResponse: { data?: unknown } | null =
          hasVisitorAgreementRead
            ? await heliumReceive(
                'getVisitorAgreement',
                [orgId, selectedVisit?.id],
                {
                  queryStringParams: { visitorId: selectedVisitor?.id },
                },
              ).catch(() => null)
            : null;

        // Guarded on kioskReadScopes because the helium GET /kiosk route
        // rejects roles without kiosk/visitor read (e.g. dashboard-only) with
        // a 403.
        const kiosksResponse: { data?: unknown } | null = hasKioskRead
          ? await heliumReceive('listAllKiosk', [orgId]).catch(() => null)
          : null;

        const locationDefaultWorkflowResponse: { data?: unknown } | null =
          await heliumReceive('locationConfig', [
            orgId,
            selectedVisit?.location?.id,
          ]).catch(() => null);

        const locationVisitorTypeWorkflowResponse: { data?: unknown } | null =
          await heliumReceive('describeVisitorTypeWorkflow', [
            orgId,
            selectedVisit?.location.id,
            selectedVisit?.visitorType.id,
          ]).catch(() => null);

        // Guarded on visitorDocumentCheckReadScopes because the helium GET
        // documentCheck route rejects roles without visitor read (e.g.
        // dashboard-only) with a 403.
        const extendedVisitorInfoResponse: { data?: unknown } | null =
          hasDocumentCheckRead
            ? await heliumReceive('getVisitDocumentCheck', [
                orgId,
                selectedVisit?.id,
              ]).catch(() => null)
            : null;

        // Guarded on visitorDeny:r because the helium GET denyEntry route
        // rejects roles without deny read (e.g. dashboard-only) with a 403.
        const denyDetailsResponse: { data?: unknown } | null = hasDenyRead
          ? await heliumReceive('getDenyEntry', [
              orgId,
              selectedVisit?.id,
            ]).catch(() => null)
          : null;

        // Guarded on qrScanLogReadScopes because the helium GET qrScanLog route
        // rejects roles without visitor/report read (e.g. dashboard-only) with
        // a 403.
        const qrCodeScanLogResponse: { data?: unknown } | null =
          hasQrScanLogRead
            ? await heliumReceive('getQrScanLog', [orgId, selectedVisit?.id], {
                queryStringParams: { visitorId: selectedVisitor?.id },
              }).catch(() => null)
            : null;

        const signedVisitorAgreementsData = Array.isArray(
          signedVisitorAgreementsResponse?.data,
        )
          ? (signedVisitorAgreementsResponse?.data as VisitorAgreement[])
          : [];
        const kiosksData = Array.isArray(kiosksResponse?.data)
          ? (kiosksResponse?.data as Kiosk[])
          : [];
        const locationDefaultWorkflowData = Array.isArray(
          locationDefaultWorkflowResponse?.data,
        )
          ? (locationDefaultWorkflowResponse?.data as LocationWorkflowConfig[])
          : [];
        const locationVisitorTypeWorkflowData = Array.isArray(
          locationVisitorTypeWorkflowResponse?.data,
        )
          ? (locationVisitorTypeWorkflowResponse?.data as VisitorTypeWorkflowConfig[])
          : [];
        const extendedVisitorInfoData = Array.isArray(
          extendedVisitorInfoResponse?.data,
        )
          ? (extendedVisitorInfoResponse?.data as DocumentCheck[])
          : [];
        const denyEntryData = Array.isArray(denyDetailsResponse?.data)
          ? (denyDetailsResponse?.data as DenyEntryResponseItem[])
          : [];
        const qrCodeData = Array.isArray(qrCodeScanLogResponse?.data)
          ? (qrCodeScanLogResponse?.data as QrCodeScanLog[])
          : [];

        // Process signed agreements - filter to the selected visitor only,
        // since the API may return agreements for other visitors on the
        // same visit even when a visitorId query param is passed.
        const signedAgreementsData = signedVisitorAgreementsData
          .filter(
            (signedAgreement) =>
              signedAgreement.visitor?.id === selectedVisitor?.id,
          )
          .map((signedAgreement) => signedAgreement.document);
        setSignedAgreements(signedAgreementsData);

        // Process kiosks - filter locally first to minimize API calls
        const allKiosks: Kiosk[] = kiosksData;
        const validKiosks = allKiosks.filter(
          (kiosk) =>
            kiosk.status.id === 1 && kiosk.appearance?.[0]?.badgePrinter === 1,
        );
        const matchingKiosks = validKiosks.filter(
          (kiosk) => kiosk.location?.id === selectedVisit?.location.id,
        );

        // Only fetch kiosk locations for kiosks that don't already match
        const nonMatchingKiosks = validKiosks.filter(
          (kiosk) => kiosk.location?.id !== selectedVisit?.location.id,
        );
        const additionalKiosks: Kiosk[] = [];
        if (nonMatchingKiosks.length > 0) {
          const kioskLocationResults = await Promise.all(
            nonMatchingKiosks.map(async (kiosk) => {
              try {
                const siteResponse = await heliumReceive('getKioskLocations', [
                  orgId,
                  kiosk.id,
                ]);
                const siteData = Array.isArray(siteResponse?.data)
                  ? (siteResponse.data as KioskLocation[])
                  : [];
                if (
                  siteData.some(
                    (site) => site.id === selectedVisit?.location.id,
                  )
                ) {
                  return kiosk;
                }
              } catch {
                // Error fetching sites for kiosk
              }
              return null;
            }),
          );
          additionalKiosks.push(
            ...kioskLocationResults.filter((k): k is Kiosk => k !== null),
          );
        }

        const finalKiosks = [
          ...new Map(
            [...matchingKiosks, ...additionalKiosks].map((kiosk) => [
              kiosk.id,
              kiosk,
            ]),
          ).values(),
        ];
        setConnectedKiosks(finalKiosks);

        // Process workflow data for agreements
        const defaultWorkflowData = locationDefaultWorkflowData[0];
        const visitorTypeWorkflowData =
          locationVisitorTypeWorkflowData[0]?.workflow;

        const attendantWorkflowId =
          visitorTypeWorkflowData?.SignInWorkflowId !== -1
            ? visitorTypeWorkflowData?.SignInWorkflowId
            : defaultWorkflowData?.SignInWorkflowId;

        const kioskWorkflowId =
          visitorTypeWorkflowData?.KioskInWorkflowId !== -1
            ? visitorTypeWorkflowData?.KioskInWorkflowId
            : defaultWorkflowData?.KioskInWorkflowId;

        // Fetch workflow documents in parallel
        const [attendantAgreementsResponse, kioskAgreementsResponse] =
          await Promise.all([
            heliumReceive('getVisitorWorkflowDocuments', [
              orgId,
              attendantWorkflowId,
            ]),
            heliumReceive('getVisitorWorkflowDocuments', [
              orgId,
              kioskWorkflowId,
            ]),
          ]);

        const attendantAgreementsData = Array.isArray(
          attendantAgreementsResponse?.data,
        )
          ? (attendantAgreementsResponse.data as Agreement[])
          : [];
        const kioskAgreementsData = Array.isArray(kioskAgreementsResponse?.data)
          ? (kioskAgreementsResponse.data as Agreement[])
          : [];

        setAttendantAgreements(attendantAgreementsData);
        setKioskAgreements(kioskAgreementsData);

        // Process extended visitor info
        const filteredExtendedVisitorInfo =
          extendedVisitorInfoData.find(
            (visitor) => visitor.id === selectedVisitor?.id,
          ) ?? null;
        setExtendedVisitorInfo(filteredExtendedVisitorInfo);

        // Fetch red flags if needed. Guarded on redFlagReadScopes because the
        // helium GET /redflag route rejects roles without red flag read
        // (e.g. dashboard-only) with a 403.
        if (hasRedFlagRead && redFlags.length === 0) {
          dispatch(fetchRedFlags({ orgId }));
        }

        // Fetch visitor photo if available
        if (filteredExtendedVisitorInfo?.photo) {
          try {
            const response = await heliumSend(
              'getVisitPhotoSignedUrl',
              [orgId, selectedVisit?.id, selectedVisitor?.id],
              {},
            );

            if (response.errorMessage) {
              throw new Error(response.errorMessage);
            }

            // New API returns Signed URL
            const photoData = response.data as {
              url?: string;
              contentType?: string;
              expiresAt?: number;
            } | null;
            const photoUrl = photoData?.url;

            setVisitorPhotoUrl(photoUrl || null);
          } catch {
            // Failed to fetch visitor photo
          }
        }

        // Process deny details
        const denyDetailsData = denyEntryData[0]?.DenyDetails || [];
        const visitorMatch = denyDetailsData.find((denyDetail) =>
          denyDetail.visitor.some(
            (visitor) => visitor.id === selectedVisitor?.id,
          ),
        );
        if (visitorMatch) {
          setDenyDetails(visitorMatch);
        }

        // Process QR code scan logs — filter to the selected visitor only
        setQrCodeScanLogs(
          qrCodeData.filter((log) => log.visitorId === selectedVisitor?.id),
        );
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };

    if (!selectedVisit || !selectedVisitor) {
      setLoading(false);
      return;
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, open]);

  const tabItems = [
    {
      key: 'profile',
      label: t('visitors.profile.tabs.profile'),
      children: (
        <ProfileTab
          visitorPhotoUrl={visitorPhotoUrl}
          denyDetails={denyDetails}
          connectedKiosks={connectedKiosks}
          hasDenyRead={hasDenyRead}
        />
      ),
    },
    // Hidden without documentCheck read access because the agreements tab
    // relies on the guarded getVisitDocumentCheck data (otherwise empty).
    ...(hasDocumentCheckRead
      ? [
          {
            key: 'agreements',
            label: t('visitors.profile.tabs.agreements'),
            children: (
              <AgreementsTab
                extendedVisitorInfo={extendedVisitorInfo!}
                attendantAgreements={attendantAgreements}
                kioskAgreements={kioskAgreements}
                signedAgreements={signedAgreements}
              />
            ),
          },
        ]
      : []),
    // Hidden without qrScanLog read access because the tab relies on the
    // guarded getQrScanLog data (otherwise empty).
    ...(hasQrScanLogRead
      ? [
          {
            key: 'qrCodeScanLog',
            label: t('visitors.profile.tabs.qrCode'),
            children: <QrCodeTab qrCodeScanLogs={qrCodeScanLogs} />,
          },
        ]
      : []),
    ...(isAltaVideoInstalled && devicesByWorkflow
      ? [
          {
            key: 'activityLog',
            label: t('visitors.profile.tabs.videoLog'),
            children: <ActivityLogTab />,
          },
        ]
      : []),
  ];

  const handleRedFlagBannerClick = () => {
    dispatch(setSelectedRedFlaggedVisitor(selectedVisitor));
    setIsRedFlagMatchModalOpen(true);
  };

  return (
    <OpDrawer
      size={DRAWER_WIDTH}
      title={t('visitors.profile.title')}
      placement="right"
      onClose={onClose}
      open={open}
      extra={!loading && <ProfileDrawerHeader onClose={onClose} />}
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <OpSpin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        </div>
      ) : (
        <>
          {selectedVisitor &&
            isVisitorRedFlagged(selectedVisitor, redFlags) && (
              <>
                <OpBanner
                  type="error"
                  style={{ marginBottom: '20px', cursor: 'pointer' }}
                  showIcon={false}
                  title={
                    <button
                      type="button"
                      onClick={handleRedFlagBannerClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: 'inherit',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <img
                          src={warningIcon}
                          alt="Warning"
                          style={{
                            width: '20px',
                            height: '20px',
                            marginRight: '8px',
                          }}
                        />
                        {t('visitors.profile.banner.redFlag')}
                      </span>
                      <EyeOutlined />
                    </button>
                  }
                />
                {isRedFlagMatchModalOpen && (
                  <RedFlagMatchModal
                    visible={isRedFlagMatchModalOpen}
                    onClose={() => setIsRedFlagMatchModalOpen(false)}
                  />
                )}
              </>
            )}
          <OpTabs
            activeKey={activeKey}
            onChange={setActiveKey}
            items={tabItems}
          />
        </>
      )}
    </OpDrawer>
  );
};

export default ProfileDrawer;
